import { logger } from '@awpmarine/core/logger';
import { z } from '@awpmarine/core/zod';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Link } from '@tanstack/react-router';
import { CheckCircleIcon } from 'lucide-react';
import * as React from 'react';

import { submitWith, useSchemaForm } from '@/components/form';
import { client } from '@/lib/api';
import { m } from '@/paraglide/messages';
import type { MessageFunction } from '@/paraglide/runtime';

/** What the message is about; the choice becomes the email's subject, so it stays in English. */
export const contactTopics = [
  'Inspection or survey enquiry',
  'Audit or assessment',
  'Careers',
  'Accounts and invoices',
  'Something else',
] as const;

/** What each topic reads as in the page's own language. */
const topicLabels: Record<(typeof contactTopics)[number], MessageFunction> = {
  'Inspection or survey enquiry': m.contact_topic_inspection,
  'Audit or assessment': m.contact_topic_audit,
  'Careers': m.contact_topic_careers,
  'Accounts and invoices': m.contact_topic_accounts,
  'Something else': m.contact_topic_other,
};

// Errors are functions, so they read in the page's locale when validation runs.
const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { error: () => m.contact_name_required() }),
  company: z.string().trim().optional(),
  email: z.email({ error: () => m.contact_email_invalid() }),
  topic: z.enum(contactTopics),
  message: z
    .string()
    .trim()
    .min(10, { error: () => m.contact_message_too_short() }),
});

export function ContactPageForm() {
  const [submitted, setSubmitted] = React.useState(false);

  const form = useSchemaForm({
    schema: formSchema,
    defaultValues: {
      name: '',
      company: '',
      email: '',
      topic: contactTopics[0],
      message: '',
    },
    onSubmitAsync: async ({ value: { topic, company, ...data } }) => {
      try {
        await client.marketing.contact({ ...data, company: company || null, subject: topic });
      } catch (error) {
        logger.error('Contact form submission failed: {error}', { error });
        return m.contact_failed();
      }
    },
    onSubmit: () => setSubmitted(true),
  });

  if (submitted) {
    return (
      <div role='status' className='flex flex-col items-start gap-3 rounded-[28px] bg-white p-10'>
        <CheckCircleIcon className='size-8 text-[#1d7a46]' />
        <h2 className='text-[26px] font-bold'>{m.contact_sent_title()}</h2>
        <p className='text-body'>{m.contact_sent_text()}</p>
      </div>
    );
  }

  return (
    <form
      method='post'
      noValidate
      onSubmit={submitWith(form)}
      className='grid grid-cols-1 gap-5 rounded-[28px] bg-white p-6 shadow-[0_24px_60px_-30px_rgba(19,36,61,0.25)] sm:grid-cols-2 sm:p-10'
    >
      <h2 className='text-[26px] font-bold sm:col-span-2'>{m.contact_form_title()}</h2>
      <form.AppField name='name'>
        {(field) => (
          <field.TextField label={m.contact_name()} placeholder={m.contact_name_placeholder()} autoComplete='name' />
        )}
      </form.AppField>
      <form.AppField name='company'>
        {(field) => (
          <field.TextField
            label={
              <>
                {m.contact_company()} <span className='text-subtle font-normal'>{m.contact_optional()}</span>
              </>
            }
            placeholder={m.contact_company_placeholder()}
            autoComplete='organization'
          />
        )}
      </form.AppField>
      <form.AppField name='email'>
        {(field) => (
          <field.TextField
            label={m.contact_email_label()}
            type='email'
            placeholder={m.contact_email_placeholder()}
            autoComplete='email'
            className='sm:col-span-2'
          />
        )}
      </form.AppField>
      <form.AppField name='topic'>
        {(field) => <field.SelectField label={m.contact_topic()} options={topicLabels} className='sm:col-span-2' />}
      </form.AppField>
      <form.AppField name='message'>
        {(field) => (
          <field.TextareaField
            label={m.contact_message()}
            placeholder={m.contact_message_placeholder()}
            className='sm:col-span-2'
          />
        )}
      </form.AppField>
      <form.AppForm>
        <form.FormError className='sm:col-span-2' />
        <div className='flex flex-wrap items-center justify-between gap-4 sm:col-span-2'>
          <p className='text-subtle max-w-80 text-sm'>
            <ParaglideMessage
              message={m.contact_privacy}
              inputs={{}}
              markup={{
                link: ({ children }) => (
                  <Link
                    to='/legal/$slug'
                    params={{ slug: 'privacy' }}
                    className='underline underline-offset-4 hover:text-brand'
                  >
                    {children}
                  </Link>
                ),
              }}
            />
          </p>
          <form.SubmitButton>{m.contact_send()}</form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  );
}
